// @ts-nocheck: Legacy page using mock data. Will be rewritten to use real API services.
import { useState } from 'react';
import {
  Globe,
  CreditCard,
  Mail,
  Shield,
  Bell,
  Database,
  Code,
  RefreshCw,
  Check,
  Info,
  AlertTriangle,
  CheckCircle,
} from 'lucide-react';
import { cn } from '@lib/utils';
import {
  mockGeneralSettings,
  mockEmailSettings,
  mockSmsSettings,
  mockSecuritySettings,
  mockNotificationSettings,
  mockBackupSettings,
  mockBackupHistory,
  mockApiSettings,
} from '@mocks/settings.mock';

type SettingsTab =
  | 'general'
  | 'payment'
  | 'email-sms'
  | 'security'
  | 'notifications'
  | 'backup'
  | 'api';

interface SettingsNavItem {
  id: SettingsTab;
  label: string;
  icon: React.ElementType;
}

const settingsNav: SettingsNavItem[] = [
  { id: 'general', label: 'General', icon: Globe },
  { id: 'payment', label: 'Payment Integrations', icon: CreditCard },
  { id: 'email-sms', label: 'Email & SMS', icon: Mail },
  { id: 'security', label: 'Security', icon: Shield },
  { id: 'notifications', label: 'Notifications', icon: Bell },
  { id: 'backup', label: 'Backup', icon: Database },
  { id: 'api', label: 'API', icon: Code },
];

export const SettingsPage = () => {
  const [activeTab, setActiveTab] = useState<SettingsTab>('general');
  const [generalSettings, setGeneralSettings] = useState(mockGeneralSettings);
  const [emailSettings, setEmailSettings] = useState(mockEmailSettings);
  const [smsSettings, setSmsSettings] = useState(mockSmsSettings);
  const [securitySettings, setSecuritySettings] = useState(mockSecuritySettings);
  const [notificationSettings, setNotificationSettings] = useState(mockNotificationSettings);
  const [backupSettings, setBackupSettings] = useState(mockBackupSettings);
  const [apiSettings, setApiSettings] = useState(mockApiSettings);

  const handleSaveAll = () => {
    alert('All settings saved successfully!');
  };

  const handleResetDefaults = () => {
    if (window.confirm('Are you sure you want to reset all settings to defaults?')) {
      alert('Settings reset to defaults');
    }
  };

  const toggleSetting = (key: keyof typeof securitySettings) => {
    setSecuritySettings((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const toggleNotification = (key: keyof typeof notificationSettings) => {
    setNotificationSettings((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const toggleBackup = (key: keyof typeof backupSettings) => {
    setBackupSettings((prev) => ({
      ...prev,
      [key]: typeof prev[key] === 'boolean' ? !prev[key] : prev[key],
    }));
  };

  const toggleApiSetting = (key: keyof typeof apiSettings) => {
    setApiSettings((prev) => ({
      ...prev,
      [key]: typeof prev[key] === 'boolean' ? !prev[key] : prev[key],
    }));
  };

  return (
    <div className="p-8">
      {/* Page Header */}
      <div className="flex items-start justify-between mb-8 flex-wrap gap-4">
        <div>
          <h1 className="font-display text-[32px] font-medium tracking-[-0.02em] text-[var(--text-primary)]">
            Platform Settings
          </h1>
          <p className="text-[14px] text-[var(--text-muted)] mt-1">
            Configure system, integrations, and platform preferences
          </p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={handleResetDefaults}
            className="inline-flex items-center gap-2 rounded-full border border-[var(--border-default)] bg-white px-5 py-2.5 text-[14px] font-medium text-[var(--text-primary)] hover:border-[var(--border-strong)] transition-all duration-200"
          >
            <RefreshCw className="w-4 h-4" strokeWidth={1.5} />
            Reset to Defaults
          </button>
          <button
            onClick={handleSaveAll}
            className="inline-flex items-center gap-2 rounded-full bg-[var(--brand-forest)] px-5 py-2.5 text-[14px] font-semibold text-white transition-all duration-200 hover:brightness-110 hover:shadow-[0_12px_32px_rgba(124,179,66,0.45)] active:brightness-95"
          >
            <Check className="w-4 h-4" strokeWidth={1.5} />
            Save All Changes
          </button>
        </div>
      </div>

      {/* Settings Layout */}
      <div className="flex gap-6">
        {/* Settings Navigation */}
        <aside className="w-[280px] flex-shrink-0">
          <div className="rounded-3xl bg-white shadow-soft p-3 sticky top-8">
            {settingsNav.map((item) => {
              const Icon = item.icon;
              const isActive = activeTab === item.id;

              return (
                <button
                  key={item.id}
                  onClick={() => setActiveTab(item.id)}
                  className={cn(
                    'w-full flex items-center gap-3 px-4 py-3 rounded-2xl text-[14px] font-medium transition-all mb-1 last:mb-0',
                    isActive
                      ? 'font-semibold text-[var(--text-primary)] bg-[var(--bg-elevated)] border-l-2 border-[var(--gradient-to)]'
                      : 'text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-base)]'
                  )}
                >
                  <Icon className="w-5 h-5" strokeWidth={1.5} />
                  <span>{item.label}</span>
                </button>
              );
            })}
          </div>
        </aside>

        {/* Settings Content */}
        <div className="flex-1">
          {/* General Settings */}
          {activeTab === 'general' && (
            <div className="rounded-3xl bg-white shadow-soft p-8 mb-6">
              <div className="border-b border-[var(--border-subtle)] pb-5 mb-6">
                <h2 className="text-[20px] font-semibold text-[var(--text-primary)]">General Settings</h2>
                <p className="text-[14px] text-[var(--text-muted)] mt-0.5">
                  Configure basic platform information and preferences
                </p>
              </div>

              <div className="grid grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="text-[11px] font-semibold uppercase tracking-[0.1em] text-[var(--text-muted)] mb-2 block">
                    Platform Name
                  </label>
                  <input
                    type="text"
                    value={generalSettings.platformName}
                    onChange={(e) =>
                      setGeneralSettings({ ...generalSettings, platformName: e.target.value })
                    }
                    className="w-full rounded-2xl border border-[var(--border-default)] bg-white px-4 py-2.5 text-[14px] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:border-[var(--border-strong)] transition-colors"
                  />
                  <p className="text-[12px] text-[var(--text-muted)] mt-1.5">Displayed across the platform</p>
                </div>

                <div>
                  <label className="text-[11px] font-semibold uppercase tracking-[0.1em] text-[var(--text-muted)] mb-2 block">
                    Support Email
                  </label>
                  <input
                    type="email"
                    value={generalSettings.supportEmail}
                    onChange={(e) =>
                      setGeneralSettings({ ...generalSettings, supportEmail: e.target.value })
                    }
                    className="w-full rounded-2xl border border-[var(--border-default)] bg-white px-4 py-2.5 text-[14px] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:border-[var(--border-strong)] transition-colors"
                  />
                  <p className="text-[12px] text-[var(--text-muted)] mt-1.5">Customer support email address</p>
                </div>

                <div>
                  <label className="text-[11px] font-semibold uppercase tracking-[0.1em] text-[var(--text-muted)] mb-2 block">
                    Support Phone
                  </label>
                  <input
                    type="tel"
                    value={generalSettings.supportPhone}
                    onChange={(e) =>
                      setGeneralSettings({ ...generalSettings, supportPhone: e.target.value })
                    }
                    className="w-full rounded-2xl border border-[var(--border-default)] bg-white px-4 py-2.5 text-[14px] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:border-[var(--border-strong)] transition-colors"
                  />
                  <p className="text-[12px] text-[var(--text-muted)] mt-1.5">Include country code for Kenya</p>
                </div>

                <div>
                  <label className="text-[11px] font-semibold uppercase tracking-[0.1em] text-[var(--text-muted)] mb-2 block">
                    Time Zone
                  </label>
                  <select
                    value={generalSettings.timeZone}
                    onChange={(e) => setGeneralSettings({ ...generalSettings, timeZone: e.target.value })}
                    className="w-full rounded-2xl border border-[var(--border-default)] bg-white px-4 py-2.5 text-[14px] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:border-[var(--border-strong)] transition-colors"
                  >
                    <option>Africa/Nairobi (EAT)</option>
                    <option>UTC</option>
                    <option>Africa/Lagos</option>
                    <option>Europe/London</option>
                  </select>
                  <p className="text-[12px] text-[var(--text-muted)] mt-1.5">Used for all timestamps</p>
                </div>

                <div>
                  <label className="text-[11px] font-semibold uppercase tracking-[0.1em] text-[var(--text-muted)] mb-2 block">
                    Default Currency
                  </label>
                  <select
                    value={generalSettings.defaultCurrency}
                    onChange={(e) =>
                      setGeneralSettings({ ...generalSettings, defaultCurrency: e.target.value })
                    }
                    className="w-full rounded-2xl border border-[var(--border-default)] bg-white px-4 py-2.5 text-[14px] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:border-[var(--border-strong)] transition-colors"
                  >
                    <option>KES - Kenyan Shilling</option>
                    <option>USD - US Dollar</option>
                    <option>EUR - Euro</option>
                    <option>GBP - British Pound</option>
                  </select>
                  <p className="text-[12px] text-[var(--text-muted)] mt-1.5">Primary currency for transactions</p>
                </div>

                <div>
                  <label className="text-[11px] font-semibold uppercase tracking-[0.1em] text-[var(--text-muted)] mb-2 block">
                    Default Language
                  </label>
                  <select
                    value={generalSettings.defaultLanguage}
                    onChange={(e) =>
                      setGeneralSettings({ ...generalSettings, defaultLanguage: e.target.value })
                    }
                    className="w-full rounded-2xl border border-[var(--border-default)] bg-white px-4 py-2.5 text-[14px] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:border-[var(--border-strong)] transition-colors"
                  >
                    <option>English</option>
                    <option>Swahili</option>
                  </select>
                  <p className="text-[12px] text-[var(--text-muted)] mt-1.5">Default platform language</p>
                </div>
              </div>

              <div className="mb-6">
                <label className="text-[11px] font-semibold uppercase tracking-[0.1em] text-[var(--text-muted)] mb-2 block">
                  Platform Description
                </label>
                <textarea
                  value={generalSettings.platformDescription}
                  onChange={(e) =>
                    setGeneralSettings({ ...generalSettings, platformDescription: e.target.value })
                  }
                  rows={4}
                  className="w-full rounded-2xl border border-[var(--border-default)] bg-white px-4 py-2.5 text-[14px] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:border-[var(--border-strong)] transition-colors resize-none"
                />
                <p className="text-[12px] text-[var(--text-muted)] mt-1.5">
                  Used in meta descriptions and marketing materials
                </p>
              </div>

              <div className="pt-6 border-t border-[var(--border-subtle)] flex justify-end gap-3">
                <button className="rounded-full border border-[var(--border-default)] bg-white px-5 py-2.5 text-[14px] font-medium text-[var(--text-primary)] hover:border-[var(--border-strong)] transition-all duration-200">
                  Cancel
                </button>
                <button className="rounded-full bg-[var(--brand-forest)] px-5 py-2.5 text-[14px] font-semibold text-white transition-all duration-200 hover:brightness-110 hover:shadow-[0_12px_32px_rgba(124,179,66,0.45)] active:brightness-95">
                  Save General Settings
                </button>
              </div>
            </div>
          )}

          {/* Payment Integrations */}
          {activeTab === 'payment' && (
            <div className="rounded-3xl bg-white shadow-soft p-8 mb-6">
              <div className="border-b border-[var(--border-subtle)] pb-5 mb-6">
                <h2 className="text-[20px] font-semibold text-[var(--text-primary)]">Payment Integrations</h2>
                <p className="text-[14px] text-[var(--text-muted)] mt-0.5">
                  Configure M-Pesa, Airtel Money, and other payment providers
                </p>
              </div>

              {/* Info banner */}
              <div className="rounded-2xl bg-[var(--bg-elevated)] border border-[var(--border-default)] p-4 mb-6 flex items-start gap-3">
                <Info className="w-5 h-5 text-[var(--text-accent)] flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                <div>
                  <div className="text-[14px] font-semibold text-[var(--text-primary)] mb-1">
                    Kenyan Mobile Money Integration
                  </div>
                  <div className="text-[13px] text-[var(--text-secondary)]">
                    M-Pesa and Airtel Money are essential for Kenyan customers. Ensure credentials are properly
                    configured.
                  </div>
                </div>
              </div>

              {/* M-Pesa Integration */}
              <div className="rounded-2xl border border-[var(--border-subtle)] p-5 hover:border-[var(--border-default)] transition-all mb-5">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 rounded-2xl bg-[var(--bg-base)] flex items-center justify-center text-[var(--text-primary)] font-bold text-[16px]">
                    M
                  </div>
                  <div className="flex-1">
                    <h3 className="text-[15px] font-semibold text-[var(--text-primary)]">M-Pesa STK Push</h3>
                    <p className="text-[13px] text-[var(--text-muted)]">Safaricom mobile money payment gateway</p>
                    <div className="mt-2">
                      <span className="inline-flex items-center px-3 py-1 rounded-full bg-[var(--state-success-bg)] text-[var(--state-success)] text-[12px] font-semibold">
                        Connected
                      </span>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button className="rounded-full border border-[var(--border-default)] bg-white px-4 py-2 text-[13px] font-medium text-[var(--text-primary)] hover:border-[var(--border-strong)] transition-all duration-200">
                      Configure
                    </button>
                    <button className="rounded-full border border-[var(--border-default)] bg-white px-4 py-2 text-[13px] font-medium text-[var(--text-primary)] hover:border-[var(--border-strong)] transition-all duration-200">
                      Test Connection
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-[11px] font-semibold uppercase tracking-[0.1em] text-[var(--text-muted)] mb-2 block">
                      M-Pesa Consumer Key
                    </label>
                    <input
                      type="text"
                      value="••••••••••••••••"
                      readOnly
                      className="w-full rounded-2xl border border-[var(--border-default)] bg-[var(--bg-base)] px-4 py-2.5 text-[14px] text-[var(--text-primary)] focus:border-[var(--border-strong)] transition-colors"
                    />
                  </div>
                  <div>
                    <label className="text-[11px] font-semibold uppercase tracking-[0.1em] text-[var(--text-muted)] mb-2 block">
                      M-Pesa Consumer Secret
                    </label>
                    <input
                      type="password"
                      value="••••••••••••••••"
                      readOnly
                      className="w-full rounded-2xl border border-[var(--border-default)] bg-[var(--bg-base)] px-4 py-2.5 text-[14px] text-[var(--text-primary)] focus:border-[var(--border-strong)] transition-colors"
                    />
                  </div>
                  <div>
                    <label className="text-[11px] font-semibold uppercase tracking-[0.1em] text-[var(--text-muted)] mb-2 block">
                      Business Short Code
                    </label>
                    <input
                      type="text"
                      value="174379"
                      readOnly
                      className="w-full rounded-2xl border border-[var(--border-default)] bg-[var(--bg-base)] px-4 py-2.5 text-[14px] text-[var(--text-primary)] focus:border-[var(--border-strong)] transition-colors"
                    />
                  </div>
                  <div>
                    <label className="text-[11px] font-semibold uppercase tracking-[0.1em] text-[var(--text-muted)] mb-2 block">
                      Passkey
                    </label>
                    <input
                      type="password"
                      value="••••••••••••••••"
                      readOnly
                      className="w-full rounded-2xl border border-[var(--border-default)] bg-[var(--bg-base)] px-4 py-2.5 text-[14px] text-[var(--text-primary)] focus:border-[var(--border-strong)] transition-colors"
                    />
                  </div>
                  <div>
                    <label className="text-[11px] font-semibold uppercase tracking-[0.1em] text-[var(--text-muted)] mb-2 block">
                      Environment
                    </label>
                    <select className="w-full rounded-2xl border border-[var(--border-default)] bg-white px-4 py-2.5 text-[14px] text-[var(--text-primary)] focus:border-[var(--border-strong)] transition-colors">
                      <option>Production</option>
                      <option>Sandbox</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-[11px] font-semibold uppercase tracking-[0.1em] text-[var(--text-muted)] mb-2 block">
                      Callback URL
                    </label>
                    <input
                      type="url"
                      value="https://api.cheki.co.ke/mpesa/callback"
                      readOnly
                      className="w-full rounded-2xl border border-[var(--border-default)] bg-[var(--bg-base)] px-4 py-2.5 text-[14px] text-[var(--text-primary)] focus:border-[var(--border-strong)] transition-colors"
                    />
                  </div>
                </div>
              </div>

              {/* Airtel Money Integration */}
              <div className="rounded-2xl border border-[var(--border-subtle)] p-5 hover:border-[var(--border-default)] transition-all mb-5">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 rounded-2xl bg-[var(--bg-base)] flex items-center justify-center text-[var(--text-primary)] font-bold text-[16px]">
                    A
                  </div>
                  <div className="flex-1">
                    <h3 className="text-[15px] font-semibold text-[var(--text-primary)]">Airtel Money</h3>
                    <p className="text-[13px] text-[var(--text-muted)]">Airtel mobile money payment gateway</p>
                    <div className="mt-2">
                      <span className="inline-flex items-center px-3 py-1 rounded-full bg-[var(--state-success-bg)] text-[var(--state-success)] text-[12px] font-semibold">
                        Connected
                      </span>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button className="rounded-full border border-[var(--border-default)] bg-white px-4 py-2 text-[13px] font-medium text-[var(--text-primary)] hover:border-[var(--border-strong)] transition-all duration-200">
                      Configure
                    </button>
                    <button className="rounded-full border border-[var(--border-default)] bg-white px-4 py-2 text-[13px] font-medium text-[var(--text-primary)] hover:border-[var(--border-strong)] transition-all duration-200">
                      Test Connection
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-[11px] font-semibold uppercase tracking-[0.1em] text-[var(--text-muted)] mb-2 block">
                      Client ID
                    </label>
                    <input
                      type="text"
                      value="••••••••••••••••"
                      readOnly
                      className="w-full rounded-2xl border border-[var(--border-default)] bg-[var(--bg-base)] px-4 py-2.5 text-[14px] text-[var(--text-primary)] focus:border-[var(--border-strong)] transition-colors"
                    />
                  </div>
                  <div>
                    <label className="text-[11px] font-semibold uppercase tracking-[0.1em] text-[var(--text-muted)] mb-2 block">
                      Client Secret
                    </label>
                    <input
                      type="password"
                      value="••••••••••••••••"
                      readOnly
                      className="w-full rounded-2xl border border-[var(--border-default)] bg-[var(--bg-base)] px-4 py-2.5 text-[14px] text-[var(--text-primary)] focus:border-[var(--border-strong)] transition-colors"
                    />
                  </div>
                  <div>
                    <label className="text-[11px] font-semibold uppercase tracking-[0.1em] text-[var(--text-muted)] mb-2 block">
                      Merchant ID
                    </label>
                    <input
                      type="text"
                      value="CHEKI001"
                      readOnly
                      className="w-full rounded-2xl border border-[var(--border-default)] bg-[var(--bg-base)] px-4 py-2.5 text-[14px] text-[var(--text-primary)] focus:border-[var(--border-strong)] transition-colors"
                    />
                  </div>
                  <div>
                    <label className="text-[11px] font-semibold uppercase tracking-[0.1em] text-[var(--text-muted)] mb-2 block">
                      Environment
                    </label>
                    <select className="w-full rounded-2xl border border-[var(--border-default)] bg-white px-4 py-2.5 text-[14px] text-[var(--text-primary)] focus:border-[var(--border-strong)] transition-colors">
                      <option>Production</option>
                      <option>Sandbox</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="pt-6 border-t border-[var(--border-subtle)] flex justify-end gap-3">
                <button className="rounded-full border border-[var(--border-default)] bg-white px-5 py-2.5 text-[14px] font-medium text-[var(--text-primary)] hover:border-[var(--border-strong)] transition-all duration-200">
                  Cancel
                </button>
                <button className="rounded-full bg-[var(--brand-forest)] px-5 py-2.5 text-[14px] font-semibold text-white transition-all duration-200 hover:brightness-110 hover:shadow-[0_12px_32px_rgba(124,179,66,0.45)] active:brightness-95">
                  Save Payment Settings
                </button>
              </div>
            </div>
          )}

          {/* Email & SMS Settings */}
          {activeTab === 'email-sms' && (
            <div className="rounded-3xl bg-white shadow-soft p-8 mb-6">
              <div className="border-b border-[var(--border-subtle)] pb-5 mb-6">
                <h2 className="text-[20px] font-semibold text-[var(--text-primary)]">Email & SMS Configuration</h2>
                <p className="text-[14px] text-[var(--text-muted)] mt-0.5">
                  Configure email and SMS service providers
                </p>
              </div>

              {/* Email Service */}
              <div className="rounded-2xl border border-[var(--border-subtle)] p-5 hover:border-[var(--border-default)] transition-all mb-5">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 rounded-2xl bg-[var(--bg-base)] flex items-center justify-center text-[var(--text-primary)] font-bold text-[16px]">
                    @
                  </div>
                  <div className="flex-1">
                    <h3 className="text-[15px] font-semibold text-[var(--text-primary)]">SMTP Email Service</h3>
                    <p className="text-[13px] text-[var(--text-muted)]">Transactional email delivery via SMTP</p>
                    <div className="mt-2">
                      <span className="inline-flex items-center px-3 py-1 rounded-full bg-[var(--state-success-bg)] text-[var(--state-success)] text-[12px] font-semibold">
                        Connected
                      </span>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button className="rounded-full border border-[var(--border-default)] bg-white px-4 py-2 text-[13px] font-medium text-[var(--text-primary)] hover:border-[var(--border-strong)] transition-all duration-200">
                      Configure
                    </button>
                    <button className="rounded-full border border-[var(--border-default)] bg-white px-4 py-2 text-[13px] font-medium text-[var(--text-primary)] hover:border-[var(--border-strong)] transition-all duration-200">
                      Send Test Email
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-[11px] font-semibold uppercase tracking-[0.1em] text-[var(--text-muted)] mb-2 block">
                      SMTP Host
                    </label>
                    <input
                      type="text"
                      value={emailSettings.smtpHost}
                      onChange={(e) => setEmailSettings({ ...emailSettings, smtpHost: e.target.value })}
                      className="w-full rounded-2xl border border-[var(--border-default)] bg-white px-4 py-2.5 text-[14px] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:border-[var(--border-strong)] transition-colors"
                    />
                  </div>
                  <div>
                    <label className="text-[11px] font-semibold uppercase tracking-[0.1em] text-[var(--text-muted)] mb-2 block">
                      SMTP Port
                    </label>
                    <input
                      type="number"
                      value={emailSettings.smtpPort}
                      onChange={(e) =>
                        setEmailSettings({ ...emailSettings, smtpPort: parseInt(e.target.value) })
                      }
                      className="w-full rounded-2xl border border-[var(--border-default)] bg-white px-4 py-2.5 text-[14px] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:border-[var(--border-strong)] transition-colors"
                    />
                  </div>
                  <div>
                    <label className="text-[11px] font-semibold uppercase tracking-[0.1em] text-[var(--text-muted)] mb-2 block">
                      Username
                    </label>
                    <input
                      type="email"
                      value={emailSettings.username}
                      onChange={(e) => setEmailSettings({ ...emailSettings, username: e.target.value })}
                      className="w-full rounded-2xl border border-[var(--border-default)] bg-white px-4 py-2.5 text-[14px] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:border-[var(--border-strong)] transition-colors"
                    />
                  </div>
                  <div>
                    <label className="text-[11px] font-semibold uppercase tracking-[0.1em] text-[var(--text-muted)] mb-2 block">
                      Password
                    </label>
                    <input
                      type="password"
                      value={emailSettings.password}
                      readOnly
                      className="w-full rounded-2xl border border-[var(--border-default)] bg-[var(--bg-base)] px-4 py-2.5 text-[14px] text-[var(--text-primary)] focus:border-[var(--border-strong)] transition-colors"
                    />
                  </div>
                  <div>
                    <label className="text-[11px] font-semibold uppercase tracking-[0.1em] text-[var(--text-muted)] mb-2 block">
                      From Name
                    </label>
                    <input
                      type="text"
                      value={emailSettings.fromName}
                      onChange={(e) => setEmailSettings({ ...emailSettings, fromName: e.target.value })}
                      className="w-full rounded-2xl border border-[var(--border-default)] bg-white px-4 py-2.5 text-[14px] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:border-[var(--border-strong)] transition-colors"
                    />
                  </div>
                  <div>
                    <label className="text-[11px] font-semibold uppercase tracking-[0.1em] text-[var(--text-muted)] mb-2 block">
                      From Email
                    </label>
                    <input
                      type="email"
                      value={emailSettings.fromEmail}
                      onChange={(e) => setEmailSettings({ ...emailSettings, fromEmail: e.target.value })}
                      className="w-full rounded-2xl border border-[var(--border-default)] bg-white px-4 py-2.5 text-[14px] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:border-[var(--border-strong)] transition-colors"
                    />
                  </div>
                </div>
              </div>

              {/* SMS Service */}
              <div className="rounded-2xl border border-[var(--border-subtle)] p-5 hover:border-[var(--border-default)] transition-all mb-5">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 rounded-2xl bg-[var(--bg-base)] flex items-center justify-center text-[var(--text-primary)] font-bold text-[16px]">
                    SMS
                  </div>
                  <div className="flex-1">
                    <h3 className="text-[15px] font-semibold text-[var(--text-primary)]">Africa's Talking SMS</h3>
                    <p className="text-[13px] text-[var(--text-muted)]">SMS notifications and OTP delivery</p>
                    <div className="mt-2">
                      <span className="inline-flex items-center px-3 py-1 rounded-full bg-[var(--state-success-bg)] text-[var(--state-success)] text-[12px] font-semibold">
                        Connected
                      </span>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button className="rounded-full border border-[var(--border-default)] bg-white px-4 py-2 text-[13px] font-medium text-[var(--text-primary)] hover:border-[var(--border-strong)] transition-all duration-200">
                      Configure
                    </button>
                    <button className="rounded-full border border-[var(--border-default)] bg-white px-4 py-2 text-[13px] font-medium text-[var(--text-primary)] hover:border-[var(--border-strong)] transition-all duration-200">
                      Send Test SMS
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-[11px] font-semibold uppercase tracking-[0.1em] text-[var(--text-muted)] mb-2 block">
                      API Username
                    </label>
                    <input
                      type="text"
                      value={smsSettings.apiUsername}
                      onChange={(e) => setSmsSettings({ ...smsSettings, apiUsername: e.target.value })}
                      className="w-full rounded-2xl border border-[var(--border-default)] bg-white px-4 py-2.5 text-[14px] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:border-[var(--border-strong)] transition-colors"
                    />
                  </div>
                  <div>
                    <label className="text-[11px] font-semibold uppercase tracking-[0.1em] text-[var(--text-muted)] mb-2 block">
                      API Key
                    </label>
                    <input
                      type="password"
                      value={smsSettings.apiKey}
                      readOnly
                      className="w-full rounded-2xl border border-[var(--border-default)] bg-[var(--bg-base)] px-4 py-2.5 text-[14px] text-[var(--text-primary)] focus:border-[var(--border-strong)] transition-colors"
                    />
                  </div>
                  <div>
                    <label className="text-[11px] font-semibold uppercase tracking-[0.1em] text-[var(--text-muted)] mb-2 block">
                      Sender ID
                    </label>
                    <input
                      type="text"
                      value={smsSettings.senderId}
                      onChange={(e) => setSmsSettings({ ...smsSettings, senderId: e.target.value })}
                      className="w-full rounded-2xl border border-[var(--border-default)] bg-white px-4 py-2.5 text-[14px] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:border-[var(--border-strong)] transition-colors"
                    />
                    <p className="text-[12px] text-[var(--text-muted)] mt-1.5">Must be approved by Africa's Talking</p>
                  </div>
                  <div>
                    <label className="text-[11px] font-semibold uppercase tracking-[0.1em] text-[var(--text-muted)] mb-2 block">
                      Environment
                    </label>
                    <select
                      value={smsSettings.environment}
                      onChange={(e) =>
                        setSmsSettings({
                          ...smsSettings,
                          environment: e.target.value as 'PRODUCTION' | 'SANDBOX',
                        })
                      }
                      className="w-full rounded-2xl border border-[var(--border-default)] bg-white px-4 py-2.5 text-[14px] text-[var(--text-primary)] focus:border-[var(--border-strong)] transition-colors"
                    >
                      <option value="PRODUCTION">Production</option>
                      <option value="SANDBOX">Sandbox</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="pt-6 border-t border-[var(--border-subtle)] flex justify-end gap-3">
                <button className="rounded-full border border-[var(--border-default)] bg-white px-5 py-2.5 text-[14px] font-medium text-[var(--text-primary)] hover:border-[var(--border-strong)] transition-all duration-200">
                  Cancel
                </button>
                <button className="rounded-full bg-[var(--brand-forest)] px-5 py-2.5 text-[14px] font-semibold text-white transition-all duration-200 hover:brightness-110 hover:shadow-[0_12px_32px_rgba(124,179,66,0.45)] active:brightness-95">
                  Save Communication Settings
                </button>
              </div>
            </div>
          )}

          {/* Security Settings */}
          {activeTab === 'security' && (
            <div className="rounded-3xl bg-white shadow-soft p-8 mb-6">
              <div className="border-b border-[var(--border-subtle)] pb-5 mb-6">
                <h2 className="text-[20px] font-semibold text-[var(--text-primary)]">Security Settings</h2>
                <p className="text-[14px] text-[var(--text-muted)] mt-0.5">
                  Configure authentication and security policies
                </p>
              </div>

              {/* Warning banner */}
              <div className="rounded-2xl bg-[var(--state-warning-bg)] border border-[var(--state-warning-border)] p-4 mb-6 flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-[var(--state-warning)] flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                <div>
                  <div className="text-[14px] font-semibold text-[var(--text-primary)] mb-1">Security Notice</div>
                  <div className="text-[13px] text-[var(--text-secondary)]">
                    Changes to security settings affect all users. Test thoroughly before applying in production.
                  </div>
                </div>
              </div>

              <ToggleGroup
                label="Two-Factor Authentication (2FA)"
                description="Require 2FA for all admin accounts"
                checked={securitySettings.twoFactorAuth}
                onChange={() => toggleSetting('twoFactorAuth')}
              />
              <ToggleGroup
                label="Email Verification"
                description="Require email verification for new users"
                checked={securitySettings.emailVerification}
                onChange={() => toggleSetting('emailVerification')}
              />
              <ToggleGroup
                label="Phone Verification"
                description="Require phone verification for merchants"
                checked={securitySettings.phoneVerification}
                onChange={() => toggleSetting('phoneVerification')}
              />
              <ToggleGroup
                label="Session Timeout"
                description="Auto-logout after inactivity"
                checked={securitySettings.sessionTimeout}
                onChange={() => toggleSetting('sessionTimeout')}
              />

              <div className="grid grid-cols-2 gap-6 mt-6">
                <div>
                  <label className="text-[11px] font-semibold uppercase tracking-[0.1em] text-[var(--text-muted)] mb-2 block">
                    Session Timeout Duration
                  </label>
                  <select
                    value={securitySettings.sessionTimeoutDuration}
                    onChange={(e) =>
                      setSecuritySettings({ ...securitySettings, sessionTimeoutDuration: e.target.value })
                    }
                    className="w-full rounded-2xl border border-[var(--border-default)] bg-white px-4 py-2.5 text-[14px] text-[var(--text-primary)] focus:border-[var(--border-strong)] transition-colors"
                  >
                    <option>15 minutes</option>
                    <option>30 minutes</option>
                    <option>1 hour</option>
                    <option>2 hours</option>
                    <option>4 hours</option>
                  </select>
                </div>
                <div>
                  <label className="text-[11px] font-semibold uppercase tracking-[0.1em] text-[var(--text-muted)] mb-2 block">
                    Password Expiry
                  </label>
                  <select
                    value={securitySettings.passwordExpiry}
                    onChange={(e) =>
                      setSecuritySettings({ ...securitySettings, passwordExpiry: e.target.value })
                    }
                    className="w-full rounded-2xl border border-[var(--border-default)] bg-white px-4 py-2.5 text-[14px] text-[var(--text-primary)] focus:border-[var(--border-strong)] transition-colors"
                  >
                    <option>Never</option>
                    <option>30 days</option>
                    <option>60 days</option>
                    <option>90 days</option>
                  </select>
                </div>
                <div>
                  <label className="text-[11px] font-semibold uppercase tracking-[0.1em] text-[var(--text-muted)] mb-2 block">
                    Minimum Password Length
                  </label>
                  <input
                    type="number"
                    min="6"
                    max="20"
                    value={securitySettings.minPasswordLength}
                    onChange={(e) =>
                      setSecuritySettings({
                        ...securitySettings,
                        minPasswordLength: parseInt(e.target.value),
                      })
                    }
                    className="w-full rounded-2xl border border-[var(--border-default)] bg-white px-4 py-2.5 text-[14px] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:border-[var(--border-strong)] transition-colors"
                  />
                </div>
                <div>
                  <label className="text-[11px] font-semibold uppercase tracking-[0.1em] text-[var(--text-muted)] mb-2 block">
                    Max Login Attempts
                  </label>
                  <input
                    type="number"
                    min="3"
                    max="10"
                    value={securitySettings.maxLoginAttempts}
                    onChange={(e) =>
                      setSecuritySettings({
                        ...securitySettings,
                        maxLoginAttempts: parseInt(e.target.value),
                      })
                    }
                    className="w-full rounded-2xl border border-[var(--border-default)] bg-white px-4 py-2.5 text-[14px] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:border-[var(--border-strong)] transition-colors"
                  />
                </div>
              </div>

              <div className="pt-6 border-t border-[var(--border-subtle)] flex justify-end gap-3 mt-6">
                <button className="rounded-full border border-[var(--border-default)] bg-white px-5 py-2.5 text-[14px] font-medium text-[var(--text-primary)] hover:border-[var(--border-strong)] transition-all duration-200">
                  Cancel
                </button>
                <button className="rounded-full bg-[var(--brand-forest)] px-5 py-2.5 text-[14px] font-semibold text-white transition-all duration-200 hover:brightness-110 hover:shadow-[0_12px_32px_rgba(124,179,66,0.45)] active:brightness-95">
                  Save Security Settings
                </button>
              </div>
            </div>
          )}

          {/* Notification Settings */}
          {activeTab === 'notifications' && (
            <div className="rounded-3xl bg-white shadow-soft p-8 mb-6">
              <div className="border-b border-[var(--border-subtle)] pb-5 mb-6">
                <h2 className="text-[20px] font-semibold text-[var(--text-primary)]">Notification Preferences</h2>
                <p className="text-[14px] text-[var(--text-muted)] mt-0.5">Configure system notification settings</p>
              </div>

              <ToggleGroup
                label="Order Notifications"
                description="Send email notifications for new orders"
                checked={notificationSettings.orderNotifications}
                onChange={() => toggleNotification('orderNotifications')}
              />
              <ToggleGroup
                label="KYC Approval Notifications"
                description="Notify merchants of KYC status changes"
                checked={notificationSettings.kycApprovalNotifications}
                onChange={() => toggleNotification('kycApprovalNotifications')}
              />
              <ToggleGroup
                label="Withdrawal Notifications"
                description="Email notifications for withdrawal requests"
                checked={notificationSettings.withdrawalNotifications}
                onChange={() => toggleNotification('withdrawalNotifications')}
              />
              <ToggleGroup
                label="Dispute Notifications"
                description="Alert admins of new disputes"
                checked={notificationSettings.disputeNotifications}
                onChange={() => toggleNotification('disputeNotifications')}
              />
              <ToggleGroup
                label="Daily Summary Reports"
                description="Daily email summary of platform activity"
                checked={notificationSettings.dailySummaryReports}
                onChange={() => toggleNotification('dailySummaryReports')}
              />
              <ToggleGroup
                label="SMS Notifications"
                description="Send SMS for critical alerts"
                checked={notificationSettings.smsNotifications}
                onChange={() => toggleNotification('smsNotifications')}
              />

              <div className="pt-6 border-t border-[var(--border-subtle)] flex justify-end gap-3 mt-6">
                <button className="rounded-full border border-[var(--border-default)] bg-white px-5 py-2.5 text-[14px] font-medium text-[var(--text-primary)] hover:border-[var(--border-strong)] transition-all duration-200">
                  Cancel
                </button>
                <button className="rounded-full bg-[var(--brand-forest)] px-5 py-2.5 text-[14px] font-semibold text-white transition-all duration-200 hover:brightness-110 hover:shadow-[0_12px_32px_rgba(124,179,66,0.45)] active:brightness-95">
                  Save Notification Settings
                </button>
              </div>
            </div>
          )}

          {/* Backup Settings */}
          {activeTab === 'backup' && (
            <div className="rounded-3xl bg-white shadow-soft p-8 mb-6">
              <div className="border-b border-[var(--border-subtle)] pb-5 mb-6">
                <h2 className="text-[20px] font-semibold text-[var(--text-primary)]">Backup & Maintenance</h2>
                <p className="text-[14px] text-[var(--text-muted)] mt-0.5">
                  Configure automated backups and system maintenance
                </p>
              </div>

              {/* Success banner */}
              <div className="rounded-2xl bg-[var(--state-success-bg)] border border-[var(--state-success-border)] p-4 mb-6 flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-[var(--state-success)] flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                <div>
                  <div className="text-[14px] font-semibold text-[var(--text-primary)] mb-1">Last Backup Successful</div>
                  <div className="text-[13px] text-[var(--text-secondary)]">
                    Last automated backup completed on {backupSettings.lastBackupDate}
                  </div>
                </div>
              </div>

              <ToggleGroup
                label="Automated Backups"
                description="Enable scheduled database backups"
                checked={backupSettings.automatedBackups}
                onChange={() => toggleBackup('automatedBackups')}
              />

              <div className="grid grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="text-[11px] font-semibold uppercase tracking-[0.1em] text-[var(--text-muted)] mb-2 block">
                    Backup Frequency
                  </label>
                  <select
                    value={backupSettings.backupFrequency}
                    onChange={(e) => setBackupSettings({ ...backupSettings, backupFrequency: e.target.value })}
                    className="w-full rounded-2xl border border-[var(--border-default)] bg-white px-4 py-2.5 text-[14px] text-[var(--text-primary)] focus:border-[var(--border-strong)] transition-colors"
                  >
                    <option>Daily</option>
                    <option>Every 12 hours</option>
                    <option>Every 6 hours</option>
                    <option>Weekly</option>
                  </select>
                </div>
                <div>
                  <label className="text-[11px] font-semibold uppercase tracking-[0.1em] text-[var(--text-muted)] mb-2 block">
                    Backup Time
                  </label>
                  <input
                    type="time"
                    value={backupSettings.backupTime}
                    onChange={(e) => setBackupSettings({ ...backupSettings, backupTime: e.target.value })}
                    className="w-full rounded-2xl border border-[var(--border-default)] bg-white px-4 py-2.5 text-[14px] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:border-[var(--border-strong)] transition-colors"
                  />
                </div>
                <div>
                  <label className="text-[11px] font-semibold uppercase tracking-[0.1em] text-[var(--text-muted)] mb-2 block">
                    Retention Period
                  </label>
                  <select
                    value={backupSettings.retentionPeriod}
                    onChange={(e) =>
                      setBackupSettings({ ...backupSettings, retentionPeriod: e.target.value })
                    }
                    className="w-full rounded-2xl border border-[var(--border-default)] bg-white px-4 py-2.5 text-[14px] text-[var(--text-primary)] focus:border-[var(--border-strong)] transition-colors"
                  >
                    <option>7 days</option>
                    <option>14 days</option>
                    <option>30 days</option>
                    <option>90 days</option>
                  </select>
                </div>
                <div>
                  <label className="text-[11px] font-semibold uppercase tracking-[0.1em] text-[var(--text-muted)] mb-2 block">
                    Backup Storage
                  </label>
                  <select
                    value={backupSettings.backupStorage}
                    onChange={(e) => setBackupSettings({ ...backupSettings, backupStorage: e.target.value })}
                    className="w-full rounded-2xl border border-[var(--border-default)] bg-white px-4 py-2.5 text-[14px] text-[var(--text-primary)] focus:border-[var(--border-strong)] transition-colors"
                  >
                    <option>AWS S3</option>
                    <option>Google Cloud Storage</option>
                    <option>Local Storage</option>
                  </select>
                </div>
              </div>

              <div className="mb-6">
                <h3 className="text-[15px] font-semibold text-[var(--text-primary)] mb-4">Recent Backups</h3>
                <div>
                  {mockBackupHistory.map((backup) => (
                    <div
                      key={backup.id}
                      className="flex items-center justify-between py-4 border-b border-[var(--border-subtle)] last:border-0 hover:bg-[var(--bg-base)] px-2 rounded-xl transition-colors"
                    >
                      <div className="flex-1">
                        <div className="text-[14px] font-semibold text-[var(--text-primary)]">{backup.name}</div>
                        <div className="text-[12px] text-[var(--text-muted)] mt-0.5">
                          {backup.date} {backup.time} • {backup.type === 'AUTOMATED' ? 'Automated' : 'Manual'}
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <span className="text-[14px] font-semibold text-[var(--text-primary)] mr-3">{backup.size}</span>
                        <button className="rounded-full border border-[var(--border-default)] bg-white px-4 py-2 text-[13px] font-medium text-[var(--text-primary)] hover:border-[var(--border-strong)] transition-all duration-200">
                          Restore
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="pt-6 border-t border-[var(--border-subtle)] flex justify-end gap-3">
                <button className="rounded-full border border-[var(--border-default)] bg-white px-5 py-2.5 text-[14px] font-medium text-[var(--text-primary)] hover:border-[var(--border-strong)] transition-all duration-200">
                  Create Manual Backup
                </button>
                <button className="rounded-full bg-[var(--brand-forest)] px-5 py-2.5 text-[14px] font-semibold text-white transition-all duration-200 hover:brightness-110 hover:shadow-[0_12px_32px_rgba(124,179,66,0.45)] active:brightness-95">
                  Save Backup Settings
                </button>
              </div>
            </div>
          )}

          {/* API Settings */}
          {activeTab === 'api' && (
            <div className="rounded-3xl bg-white shadow-soft p-8 mb-6">
              <div className="border-b border-[var(--border-subtle)] pb-5 mb-6">
                <h2 className="text-[20px] font-semibold text-[var(--text-primary)]">API Configuration</h2>
                <p className="text-[14px] text-[var(--text-muted)] mt-0.5">Manage API keys and webhooks</p>
              </div>

              {/* Info banner */}
              <div className="rounded-2xl bg-[var(--bg-elevated)] border border-[var(--border-default)] p-4 mb-6 flex items-start gap-3">
                <Info className="w-5 h-5 text-[var(--text-accent)] flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                <div>
                  <div className="text-[14px] font-semibold text-[var(--text-primary)] mb-1">API Documentation</div>
                  <div className="text-[13px] text-[var(--text-secondary)]">
                    Full API documentation is available at{' '}
                    <a
                      href="https://api.cheki.co.ke/docs"
                      className="text-[var(--text-accent)] hover:text-[var(--text-accent)]"
                    >
                      https://api.cheki.co.ke/docs
                    </a>
                  </div>
                </div>
              </div>

              <div className="mb-6">
                <label className="text-[11px] font-semibold uppercase tracking-[0.1em] text-[var(--text-muted)] mb-2 block">
                  Production API Key
                </label>
                <input
                  type="text"
                  value={apiSettings.productionApiKey}
                  readOnly
                  className="w-full rounded-2xl border border-[var(--border-default)] bg-[var(--bg-base)] px-4 py-2.5 text-[14px] text-[var(--text-primary)] focus:border-[var(--border-strong)] transition-colors"
                />
                <p className="text-[12px] text-[var(--text-muted)] mt-1.5">Used for production environment</p>
              </div>

              <div className="mb-6">
                <label className="text-[11px] font-semibold uppercase tracking-[0.1em] text-[var(--text-muted)] mb-2 block">
                  Sandbox API Key
                </label>
                <input
                  type="text"
                  value={apiSettings.sandboxApiKey}
                  readOnly
                  className="w-full rounded-2xl border border-[var(--border-default)] bg-[var(--bg-base)] px-4 py-2.5 text-[14px] text-[var(--text-primary)] focus:border-[var(--border-strong)] transition-colors"
                />
                <p className="text-[12px] text-[var(--text-muted)] mt-1.5">Used for testing and development</p>
              </div>

              <ToggleGroup
                label="API Rate Limiting"
                description="Limit API requests per minute"
                checked={apiSettings.apiRateLimiting}
                onChange={() => toggleApiSetting('apiRateLimiting')}
              />

              <div className="grid grid-cols-2 gap-6 mt-6">
                <div>
                  <label className="text-[11px] font-semibold uppercase tracking-[0.1em] text-[var(--text-muted)] mb-2 block">
                    Rate Limit
                  </label>
                  <input
                    type="number"
                    value={apiSettings.rateLimit}
                    onChange={(e) => setApiSettings({ ...apiSettings, rateLimit: parseInt(e.target.value) })}
                    placeholder="Requests per minute"
                    className="w-full rounded-2xl border border-[var(--border-default)] bg-white px-4 py-2.5 text-[14px] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:border-[var(--border-strong)] transition-colors"
                  />
                </div>
                <div>
                  <label className="text-[11px] font-semibold uppercase tracking-[0.1em] text-[var(--text-muted)] mb-2 block">
                    Webhook URL
                  </label>
                  <input
                    type="url"
                    value={apiSettings.webhookUrl}
                    onChange={(e) => setApiSettings({ ...apiSettings, webhookUrl: e.target.value })}
                    placeholder="https://your-domain.com/webhook"
                    className="w-full rounded-2xl border border-[var(--border-default)] bg-white px-4 py-2.5 text-[14px] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:border-[var(--border-strong)] transition-colors"
                  />
                </div>
              </div>

              <div className="pt-6 border-t border-[var(--border-subtle)] flex justify-end gap-3 mt-6">
                <button className="rounded-full border border-[var(--border-default)] bg-white px-5 py-2.5 text-[14px] font-medium text-[var(--text-primary)] hover:border-[var(--border-strong)] transition-all duration-200">
                  Regenerate Keys
                </button>
                <button className="rounded-full bg-[var(--brand-forest)] px-5 py-2.5 text-[14px] font-semibold text-white transition-all duration-200 hover:brightness-110 hover:shadow-[0_12px_32px_rgba(124,179,66,0.45)] active:brightness-95">
                  Save API Settings
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Toggle Group Component
interface ToggleGroupProps {
  label: string;
  description: string;
  checked: boolean;
  onChange: () => void;
}

const ToggleGroup = ({ label, description, checked, onChange }: ToggleGroupProps) => {
  return (
    <div className="flex items-center justify-between py-4 border-b border-[var(--border-subtle)] last:border-0">
      <div className="flex-1">
        <div className="text-[14px] font-semibold text-[var(--text-primary)]">{label}</div>
        <div className="text-[13px] text-[var(--text-muted)] mt-0.5">{description}</div>
      </div>
      <button
        onClick={onChange}
        className={cn(
          'relative w-12 h-6 rounded-full transition-all',
          checked ? 'bg-[var(--gradient-to)]' : 'bg-[var(--bg-elevated)]'
        )}
      >
        <span
          className={cn(
            'absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform',
            checked ? 'transform translate-x-6' : ''
          )}
        />
      </button>
    </div>
  );
};